/**
 * Derives the active AI add-on tier from the company subscription and
 * exposes per-feature gating helpers consumed by the AI Agent page.
 *
 * Tier hierarchy (from least to most capable):
 *   ai_assistant  → Chat only
 *   fleet_copilot → Chat + Reports (except Daily) + Approvals + Memory
 *   autonomous    → Everything
 */
import { useMemo } from "react";
import { useGetSubscription } from "../api/billing";
import { getCurrentFleetId } from "../utils/getCurrentFleet";

export type AiTier = "ai_assistant" | "fleet_copilot" | "autonomous" | null;

export interface AiPlanGating {
  /** Raw AI add-on slug, or null if none attached. */
  tier: AiTier;
  /** Subscription is still loading. */
  loading: boolean;
  /** The user has ANY AI add-on. */
  hasAi: boolean;
  /** Tab-level access --------------------------------------------------- */
  canChat: boolean;
  canReports: boolean;
  canApprovals: boolean;
  canMemory: boolean;
  /** Inside the Reports panel ------------------------------------------- */
  canDailyReport: boolean;
}

const TIER_ORDER: Record<string, number> = {
  ai_assistant: 0,
  fleet_copilot: 1,
  autonomous: 2,
};

export const useAiPlanTier = (): AiPlanGating => {
  const fleetId = getCurrentFleetId();
  const { subscription, isPending } = useGetSubscription(fleetId);

  return useMemo(() => {
    const tier = (subscription?.ai_addon?.slug as AiTier) ?? null;
    const rank = tier ? TIER_ORDER[tier] ?? -1 : -1;

    return {
      tier,
      loading: isPending,
      hasAi: tier !== null,
      canChat: rank >= 0,              // all tiers
      canReports: rank >= 1,           // copilot + autonomous
      canApprovals: rank >= 1,         // copilot + autonomous
      canMemory: rank >= 1,            // copilot + autonomous
      canDailyReport: rank >= 2,       // autonomous only
    };
  }, [subscription, isPending]);
};
